import { useCanvasById } from "@/api/canvas";
import { useWorkspaceById } from "@/api/workspaces";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { type FlowSchedule } from "@cohesive/api-client";
import { Frame, Layers, type LucideIcon } from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface AutomationLocation {
  kind: "canvas" | "workspace";
  icon: LucideIcon;
  /** Resolved resource name, or a fallback when the lookup fails. */
  label: string;
  /** True when the canvas/workspace couldn't be read (archived, or access lost). */
  unavailable: boolean;
  open: () => void;
}

/** Where an automation runs, resolved for display and navigation. */
export function useAutomationLocation(
  schedule: Pick<FlowSchedule, "canvas_id" | "workspace_id">,
): AutomationLocation {
  const navigate = useNavigate();
  const { data: canvas, isError: canvasError } = useCanvasById(
    schedule.canvas_id,
  );
  const { data: workspace, isError: workspaceError } = useWorkspaceById(
    schedule.workspace_id,
  );

  const isCanvas = !!schedule.canvas_id;
  const unavailable = isCanvas ? canvasError : workspaceError;
  const kindLabel = isCanvas ? "Canvas" : "Workspace";
  const name = isCanvas ? canvas?.name : workspace?.name;

  const open = useCallback(() => {
    if (schedule.canvas_id) {
      navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: schedule.canvas_id }));
    } else if (schedule.workspace_id) {
      navigate(
        route(ROUTES.WORKSPACES.DETAIL, { workspaceId: schedule.workspace_id }),
      );
    }
  }, [navigate, schedule.canvas_id, schedule.workspace_id]);

  return {
    kind: isCanvas ? "canvas" : "workspace",
    icon: isCanvas ? Frame : Layers,
    label: name ?? (unavailable ? `${kindLabel} unavailable` : kindLabel),
    unavailable,
    open,
  };
}
